import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { ApiService } from '@b2b/common/services/api/types';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ACMEListResponse, ColumnDef, extractACMEPayload, TODO } from '@b2b/types/core';
import { FormsSummariesListItem } from '@b2b/types/forms';
import { MembershipDetails } from '@b2b/types/membership';
import uiRouter, { StateParams, StateService } from '@uirouter/angularjs';
import angular, { IQService, IScope, ITimeoutService } from 'angular';

type OrdersDetailFormsModel = FormsSummariesListItem & {
  membershipId: number;
  membershipLevel: string;
  membershipOffering: string;
  membershipLifecycle: string;
};

OrdersDetailFormsController.$inject = [
  '$scope',
  '$state',
  '$stateParams',
  '$timeout',
  '$q',
  'ApiService',
  'GridStateFactory',
  'GridDefinitionFactory',
  'PermissionsService',
  'HelpersService',
  'OrderService',
];

function OrdersDetailFormsController(
  $scope: IScope & { ordersDetail: TODO; ordersDetailForms: TODO },
  $state: StateService,
  $stateParams: StateParams,
  $timeout: ITimeoutService,
  $q: IQService,
  ApiService: ApiService,
  GridStateFactory: GridStateFactory,
  GridDefinitionFactory: TODO,
  PermissionsService: PermissionsService,
  HelpersService: TODO,
  OrderService: TODO,
) {
  const parentCtrl = $scope.ordersDetail;
  const ctrl = {
    model: [] as OrdersDetailFormsModel[],
    membershipsData: {},
    searchQuery: '',
    isLoaded: false,
    viewId: '' as string | undefined,
    gridState: new GridStateFactory(),

    gridName: $state.current.name + '.grid0',
    gridDefinition: new GridDefinitionFactory(),
    optionsOverride: {
      data: 'ordersDetailForms.model',
      columnDefs: [
        {
          field: 'formCategoryType',
          displayName: 'Type',
          visible: true,
          cellTemplate: `
            <div class="ui-grid-cell-contents">
              <span class="text-capitalize">{{ row.entity.formCategoryType}}</span>
            </div>
          `,
        },
        {
          field: 'itemName',
          displayName: 'Event Template Name',
          visible: true,
          onCellNavClick: (row, col) => {
            ctrl.optionsOverride.appScopeProvider.viewItem(row);
          },
          cellTemplate: `
            <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.viewItem(row)" data-ng-if="row.entity.itemType === 'Event'" role="link">
                <span class="sr-only">View Event </span>{{ row.entity.itemName }}<span class="sr-only"> Details</span>
              </a>
            </div>
          `,
        },
        {
          field: 'membershipId',
          displayName: 'Membership ID',
          visible: true,
          onCellNavClick: (row, col) => {
            ctrl.optionsOverride.appScopeProvider.viewItem(row);
          },
          cellTemplate: `
            <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.viewItem(row)" data-ng-if="row.entity.itemType !== 'Event'" role="link">
                <span class="sr-only">View Membership ID</span> {{ row.entity.membershipId }}<span class="sr-only"> Details</span>
              </a>
            </div>
          `,
        },
        {
          field: 'membershipLevel',
          displayName: 'Membership Level',
          visible: true,
        },
        {
          field: 'membershipOffering',
          displayName: 'Membership Offering',
          visible: false,
        },
        {
          field: 'membershipLifecycle',
          displayName: 'Membership Lifecycle',
          visible: false,
        },
        {
          field: 'formName',
          displayName: 'Form Name',
          visible: true,
        },
        {
          field: 'formNumber',
          displayName: 'Response Number',
          visible: true,
        },
      ] as ColumnDef[],
      appScopeProvider: {
        onDataGridPaginate: (newPage, pageSize, sortField, sortDirection) => {
          ctrl.onDataGridPaginate(newPage, pageSize, sortField, sortDirection);
        },
        onDataGridSortChange: (grid, columns, page) => {
          ctrl.onDataGridSortChange(grid, columns, page);
        },
        onGridDblClick: function (row) {
          ctrl.onGridDblClick(row);
        },
        viewItem: function (row) {
          ctrl.viewItem(row);
        },
      },
    },

    init: function () {
      ctrl.viewId = $state.current.name;
      $scope.$emit('Application::contentTitleChange', {
        title: 'Order Forms Summary',
        buttonLeft: {
          label: 'Back',
          action: function () {
            window.history.go(-1);
          },
        },
        buttonRight: {
          label: 'Edit Order',
          permissions: ['ORDER_UPDATE'],
          action: function () {
            $state.go('application.orders.edit', { orderId: parentCtrl.model.orderId });
          },
          isDisabled: parentCtrl.isDisabled(),
        },
      });

      ctrl.gridDefinition.init(ctrl.gridName, ctrl.optionsOverride, ctrl.viewId, $state.current.data.enablePreferences);

      if (PermissionsService.check('FORM_RESPONSE_VIEW')) {
        ctrl.optionsOverride.columnDefs.unshift({
          field: 'View',
          headerName: 'View',
          width: 60,
          onCellNavClick: (row, col) => {
            ctrl.optionsOverride.appScopeProvider.onGridDblClick(row);
          },
          cellTemplate: `
            <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.onGridDblClick(row)" role="link">View<span class="sr-only"> Details</span></a>
            </div>
          `,
          enableHiding: false,
          enableSorting: false,
        });
      }

      $scope.$on('DataGrid::UserPreferencesLoaded', ctrl.initData);
    },

    initData: function () {
      const props = ctrl.gridDefinition.getGridState();
      ctrl.gridState.updateGridState(props.page, props.pageSize, props.sortField, props.sortDirection);
      ctrl.getFormSummary();
    },

    getFormSummary: function () {
      ApiService.orders.forms
        .list(angular.extend({ orderId: $stateParams.orderId }, ctrl.gridState.getGridState()))
        .then(ctrl.success.init)
        .catch(ctrl.error.init);
    },

    viewItem: function (row: { entity: TODO }) {
      switch (row.entity.formCategoryType) {
        case 'event': {
          $state.go('application.programsInstance', { id: row.entity.itemId });
          break;
        }

        case 'membership': {
          $state.go('application.membership.detail', { id: row.entity.membershipId });
          break;
        }

        case 'donation':
        case 'addon':
        default:
          break;
      }

      return;
    },

    onGridDblClick: function (item) {
      if (PermissionsService.check('FORM_RESPONSE_VIEW')) {
        $state.go('application.orders.detail.forms.detail', { responseId: item.entity.formResponseId });
      }
    },

    onDataGridPaginate: function (newPage, pageSize, sortField, sortDirection) {
      ctrl.gridState.updateGridState(newPage, pageSize, sortField, sortDirection);
      ctrl.getFormSummary();
    },

    onDataGridSortChange(grid, columns, page) {
      const data = columns[0];

      if (data) {
        ctrl.gridState.updateGridState(page || null, null, data.field, data.sort.direction);
        ctrl.getFormSummary();
      }
    },

    processList: function (list: FormsSummariesListItem[]): OrdersDetailFormsModel[] {
      const formsSummariesListItemList: OrdersDetailFormsModel[] = list.map(
        (item: FormsSummariesListItem): OrdersDetailFormsModel => {
          const model = item as OrdersDetailFormsModel;
          switch (model.formCategoryType) {
            case 'membership':
              model.itemName = '';
              model.membershipLifecycle = ctrl.getMembershipLifecycle(model.itemType);

              if (model.membershipId) {
                model.membershipId = model.membershipId;
                model.membershipLevel = ctrl.membershipsData[model.membershipId].membershipCategory;
                model.membershipOffering = ctrl.membershipsData[model.membershipId].offeringName;
              }
              break;
            case 'event':
            case 'donation':
            case 'addon':
            default:
              break;
          }

          return model;
        },
      );

      return formsSummariesListItemList;
    },

    getMembershipLifecycle: function (itemType) {
      return OrderService.getItemType(itemType, true);
    },

    getMembershipData: function (list: FormsSummariesListItem[]) {
      const membershipsPromises = list
        .filter((item) => item.formCategoryType === 'membership' && item.membershipId)
        .map((item) => ApiService.membership.detail(item.membershipId));

      return Promise.all(membershipsPromises).then((responses) => {
        return responses.map(extractACMEPayload()).filter((item) => !!item);
      });
    },

    success: {
      init: function (response: { data: ACMEListResponse<FormsSummariesListItem> }) {
        const formsList = response.data.payload.list;

        ctrl
          .getMembershipData(formsList)
          .then((membershipsList: MembershipDetails[]) => {
            if (membershipsList && membershipsList.length) {
              membershipsList.forEach((membership: MembershipDetails) => {
                ctrl.membershipsData[membership.id] = {
                  ...membership,
                };
              });
            }
          })
          .finally(() => {
            ctrl.model = ctrl.processList(formsList);
            ctrl.gridDefinition.isLoaded = true;
            ctrl.gridState.setPropertyValue('totalItems', response.data.payload.pagination.count);
            ctrl.isLoaded = true;

            $timeout(() => {
              $scope.$broadcast('DataGrid::ResizeGrid', {
                name: ctrl.gridDefinition.options.gridName,
                datasetLength: ctrl.model.length,
                totalItems: response.data.payload.pagination.count,
              });
            }, 10);
          });
      },
    },

    error: {
      init: function (response) {
        console.log(response);
      },
    },
  };

  $scope.ordersDetailForms = ctrl;
  ctrl.init();
}

export const OrdersDetailFormsModule = angular
  .module('acme.view.application.orders.detail.forms.controllers', [
    uiRouter,
    'acme.common.service.api',
    'acme.common.factory.gridState',
    'acme.common.factory.gridDefinition',
    'acme.common.service.permissions',
    'acme.common.service.helpers',
    'acme.common.service.order',
  ])
  .controller('OrdersDetailFormsController', OrdersDetailFormsController).name;
